import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Data, Router, UrlTree, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject, Subscription } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { canonicalUrl } from '@blauwtand/seo-hierarchy';
import { SeoPageService } from '../services/seo-page.service';
import { SeoService } from '../services/seo.service';
import {
  SeoChildLink,
  SeoPageContentBlock,
  SeoPageDetail,
  SeoPageRelatedPage,
  SeoRouteState,
} from './seo-page.models';
import { redirectTarget } from './seo-route';
import { IconComponent } from '../icon.component';
import { BlogBlockParagraphComponent } from '../blog/blocks/block-paragraph.component';
import { BlogBlockImageComponent } from '../blog/blocks/block-image.component';
import { FeaturedJobsListComponent } from '../shared/featured-jobs-list/featured-jobs-list.component';
import { FaqBlockComponent } from '../shared/faq-block/faq-block.component';
import { jobPath } from '../vacancy-format';

type SeoPageState = 'loading' | 'ok' | 'not-found' | 'error';

/** Generic heading of the direct-child link section (ADMIN-AND-PAGES "Public navigation"). */
export const CHILDREN_SECTION_HEADING = 'Bekijk de mogelijkheden';

@Component({
    selector: 'app-seo-page',
    templateUrl: './seo-page.component.html',
    styleUrls: ['./seo-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        RouterLink,
        IconComponent,
        BlogBlockParagraphComponent,
        BlogBlockImageComponent,
        FeaturedJobsListComponent,
        FaqBlockComponent,
    ],
})
export class SeoPageComponent implements OnInit, OnDestroy {
  public state: SeoPageState = 'loading';
  public detail: SeoPageDetail | null = null;
  public errorMessage: string | null = null;
  public readonly childrenHeading = CHILDREN_SECTION_HEADING;

  private currentPath = '';
  private retrySubscription: Subscription | null = null;
  private readonly destroy$ = new Subject<void>();
  private readonly jsonLdCollectionId = 'seo-page-collection';
  private readonly jsonLdFaqId = 'seo-page-faq';
  private readonly jsonLdBreadcrumbId = 'seo-page-breadcrumb';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly api: SeoPageService,
    private readonly seo: SeoService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  /**
   * The route resolver delivers exactly one outcome per navigation; an abandoned navigation's
   * response is unsubscribed by the router and can never reach this component.
   */
  public ngOnInit(): void {
    this.route.data
      .pipe(
        map((data: Data) => data['seo'] as SeoRouteState | undefined),
        takeUntil(this.destroy$),
      )
      .subscribe((outcome) => {
        this.cancelRetry();
        if (outcome) this.apply(outcome);
      });
  }

  public ngOnDestroy(): void {
    this.cancelRetry();
    this.removePageJsonLd();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Retry after an unavailable dependency; a new navigation cancels it. */
  public retry(): void {
    const path = this.currentPath;
    if (!path) return;
    this.cancelRetry();
    this.state = 'loading';
    this.detail = null;
    this.cdr.markForCheck();
    this.retrySubscription = this.api.resolve(path).subscribe({
      next: (resolution) => {
        if (resolution?.kind === 'page') {
          this.apply({ kind: 'page', path, revision: resolution.revision, page: resolution.page });
        } else if (resolution?.kind === 'redirect' && resolution.path.startsWith('/') && resolution.path !== path) {
          void this.router.navigateByUrl(redirectTarget(this.router, this.route.snapshot, resolution.path), { replaceUrl: true });
        } else {
          this.apply({ kind: 'unavailable', path });
        }
      },
      error: (error: HttpErrorResponse) => {
        this.apply(error?.status === 404 || error?.status === 400 ? { kind: 'not-found', path } : { kind: 'unavailable', path }, error);
      },
    });
  }

  public trackChild(_index: number, child: SeoChildLink): string {
    return child.documentId;
  }

  public trackRelated(_index: number, page: SeoPageRelatedPage): string {
    return page.documentId;
  }

  public get hasVacancies(): boolean {
    return (this.detail?.vacancyBlock.jobs?.length ?? 0) > 0;
  }

  public get ctaRouterLink(): UrlTree | null {
    const url = this.detail?.cta.buttonUrl;
    if (!url) return null;
    if (this.detail?.cta.buttonKind === 'internal') return this.router.parseUrl(url);
    return null;
  }

  public get ctaExternalHref(): string | null {
    if (!this.detail?.cta.buttonUrl) return null;
    if (this.detail.cta.buttonKind === 'external') return this.detail.cta.buttonUrl;
    return null;
  }

  public get secondaryCtaRouterLink(): UrlTree | null {
    const button = this.detail?.cta.secondaryButton;
    if (!button?.url) return null;
    if (button.kind === 'internal') return this.router.parseUrl(button.url);
    return null;
  }

  public get secondaryCtaExternalHref(): string | null {
    const button = this.detail?.cta.secondaryButton;
    if (!button?.url) return null;
    if (button.kind === 'external') return button.url;
    return null;
  }

  public get vacancyCtaRouterLink(): UrlTree | null {
    return this.toRouterLink(this.detail?.vacancyBlock.cta.url);
  }

  public get vacancyCtaExternalHref(): string | null {
    return this.toExternalHref(this.detail?.vacancyBlock.cta.url);
  }

  public get emptyVacanciesCtaRouterLink(): UrlTree | null {
    return this.toRouterLink(this.detail?.vacancyBlock.empty.ctaUrl);
  }

  public get emptyVacanciesCtaExternalHref(): string | null {
    return this.toExternalHref(this.detail?.vacancyBlock.empty.ctaUrl);
  }

  /**
   * Computes the CSS `url('…')` value for the hero's custom property.
   * When the page has a custom `heroImage`, we feed that into the
   * `--seo-hero-bg` CSS variable so the SCSS layered gradient can compose
   * on top of it. Falls back to `null` so the SCSS default asset kicks in.
   */
  public get heroBgCss(): string | null {
    const url = this.detail?.intro.heroImage;
    if (!url) return null;
    return `url('${url}')`;
  }

  public trackContentBlock(index: number, block: SeoPageContentBlock): string {
    if (block.type === 'image') {
      return `${block.type}:${block.url}:${index}`;
    }
    return `${block.type}:${index}`;
  }

  private toRouterLink(url: string | null | undefined): UrlTree | null {
    if (!url) return null;
    return url.startsWith('/') ? this.router.parseUrl(url) : null;
  }

  private toExternalHref(url: string | null | undefined): string | null {
    if (!url) return null;
    return url.startsWith('/') ? null : url;
  }

  private cancelRetry(): void {
    this.retrySubscription?.unsubscribe();
    this.retrySubscription = null;
  }

  private apply(outcome: SeoRouteState, error?: unknown): void {
    this.currentPath = outcome.path;
    this.removePageJsonLd();
    if (outcome.kind === 'page') {
      this.detail = outcome.page;
      this.state = 'ok';
      this.errorMessage = null;
      this.applySeo(outcome.page);
    } else if (outcome.kind === 'not-found') {
      this.handleNotFound();
    } else {
      this.handleError(error);
    }
    this.cdr.markForCheck();
  }

  private removePageJsonLd(): void {
    this.seo.removeJsonLd(this.jsonLdCollectionId);
    this.seo.removeJsonLd(this.jsonLdFaqId);
    this.seo.removeJsonLd(this.jsonLdBreadcrumbId);
  }

  private handleNotFound(): void {
    this.state = 'not-found';
    this.detail = null;
    this.errorMessage = null;
    this.seo.updateSeo({
      title: 'Pagina niet gevonden',
      description: 'Deze pagina bestaat niet of is verplaatst.',
      robots: 'noindex,follow',
    });
  }

  private handleError(err: unknown): void {
    this.state = 'error';
    this.detail = null;
    this.errorMessage =
      err instanceof HttpErrorResponse && err.status
        ? `De pagina is tijdelijk niet beschikbaar (${err.status}). Probeer het zo opnieuw.`
        : 'De pagina is tijdelijk niet beschikbaar. Probeer het zo opnieuw.';
    this.seo.updateSeo({
      title: 'Pagina tijdelijk niet beschikbaar',
      description: 'De pagina kon niet geladen worden.',
      robots: 'noindex,follow',
    });
  }

  private applySeo(detail: SeoPageDetail): void {
    this.seo.updateSeo({
      title: detail.seo.title,
      description: detail.seo.description,
      canonicalUrl: detail.seo.canonical,
      imageUrl: detail.seo.ogImage ?? undefined,
      ogType: 'website',
      robots: detail.seo.robots,
    });

    this.seo.addJsonLd(
      this.jsonLdCollectionId,
      this.buildCollectionPageJsonLd(detail),
    );

    if (detail.faq.items.length > 0) {
      this.seo.addJsonLd(this.jsonLdFaqId, {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: detail.faq.items.map((i) => ({
          '@type': 'Question',
          name: i.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: this.stripHtml(i.answer),
          },
        })),
      });
    }

    // The single BreadcrumbList for SEO routes, from the same C06 list as the visible trail.
    this.seo.addJsonLd(this.jsonLdBreadcrumbId, this.buildBreadcrumbListJsonLd(detail));
  }

  private buildBreadcrumbListJsonLd(detail: SeoPageDetail): Record<string, unknown> {
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: detail.breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.label,
        item: canonicalUrl(crumb.path),
      })),
    };
  }

  private buildCollectionPageJsonLd(
    detail: SeoPageDetail,
  ): Record<string, unknown> {
    const jobs = detail.vacancyBlock.jobs || [];
    const itemListElement = jobs
      .filter((job) => Boolean(job?.id && job?.title))
      .map((job, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: this.seo.generateCanonical(jobPath(job)),
        name: job.title,
      }));

    return {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      url: detail.seo.canonical,
      name: detail.vacancyBlock.title || detail.title,
      description: detail.seo.description,
      inLanguage: 'nl-NL',
      // `datePublished` must be the immutable original date. Strapi re-stamps
      // `publishedAt` on every republish, so it is NOT used here (it made the
      // date drift forward on each CMS edit). `createdAt` never changes;
      // `publishedAt` remains only as a defensive fallback.
      datePublished: detail.createdAt ?? detail.publishedAt ?? undefined,
      dateModified:
        detail.updatedAt ?? detail.createdAt ?? detail.publishedAt ?? undefined,
      author: {
        '@type': 'Organization',
        name: 'Blauwtand',
        url: 'https://blauwtand.com/',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Blauwtand',
        url: 'https://blauwtand.com/',
      },
      mainEntity: {
        '@type': 'ItemList',
        numberOfItems: itemListElement.length,
        itemListElement,
      },
    };
  }

  private stripHtml(value: string): string {
    return (value || '')
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, ' ')
      .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;|&apos;/gi, "'")
      .replace(/\s+/g, ' ')
      .trim();
  }
}
